import type { ProjectWrapperType } from "@/dataProviders/ProjectWrapper";
import { mergeModelSyncState } from "./modelSyncState";

type SpeckleAttachBaseline = {
  project: ProjectWrapperType["project"] | null;
  models: ProjectWrapperType["models"] | null;
};

type ProjectWrapperCloneFns = {
  project: (project: ProjectWrapperType["project"]) => ProjectWrapperType["project"];
  models: (models: ProjectWrapperType["models"]) => ProjectWrapperType["models"];
};

/**
 * Fold what a Speckle attach or sync wrote onto the live wrapper and the save/discard baseline, and
 * nothing else (RTLCA-5850): `streamId`, `speckleData`, `modelNames`, and the models' Speckle-owned
 * columns via `mergeModelSyncState`. Everything else on `project` belongs to the reader until they
 * Save. Either half of the baseline can be null — a caller with no snapshot yet still gets the live fold.
 */
export const followSpeckleOwnedState = (
  live: ProjectWrapperType,
  baseline: SpeckleAttachBaseline,
  fresh: ProjectWrapperType,
  clone: ProjectWrapperCloneFns
): void => {
  live.project.streamId = fresh.project.streamId;
  live.project.speckleData = fresh.project.speckleData;
  live.project.modelNames = fresh.project.modelNames;
  mergeModelSyncState(live.models, fresh.models);

  if (baseline.project) {
    // Through the caller's clone, never structuredClone — see the discard test for why.
    const clonedProject = clone.project(fresh.project);
    baseline.project.streamId = clonedProject.streamId;
    baseline.project.speckleData = clonedProject.speckleData;
    baseline.project.modelNames = clonedProject.modelNames;
  }
  if (baseline.models) {
    mergeModelSyncState(baseline.models, clone.models(fresh.models));
  }
};
